import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PublicHeader from '../components/PublicHeader';
import Footer from '../components/Footer';
import { FiBook, FiShoppingCart, FiCheck, FiFilter, FiSearch, FiTag, FiClock } from 'react-icons/fi';

const AllCourses = () => {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  
  const API_BASE_URL = 'http://localhost/recc';
  const isLoggedIn = !!user;

  useEffect(() => {
    fetchCategories();
    fetchAllCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/coursecategory`);
      const data = await response.json();
      if (data.success) {
        setCategories(data.data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchAllCourses = async () => {
    try {
      setLoading(true);
      const email = isLoggedIn ? user.email : 'guest@example.com';
      const response = await fetch(`${API_BASE_URL}/api/popularcourse?email=${email}&type=all`);
      const data = await response.json();
      if (data.success) {
        setCourses(data.data);
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCoursesByCategory = async (categoryId) => {
    try {
      setLoading(true);
      const email = isLoggedIn ? user.email : 'guest@example.com';
      const url = categoryId === 'all' 
        ? `${API_BASE_URL}/api/popularcourse?email=${email}&type=all`
        : `${API_BASE_URL}/api/coursebycategory/${categoryId}?email=${email}`;
      
      const response = await fetch(url);
      const data = await response.json();
      if (data.success) {
        setCourses(data.data);
      }
      setSelectedCategory(categoryId);
    } catch (error) {
      console.error('Error fetching courses:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredCourses = courses.filter(course =>
    course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    course.tradeName?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <PublicHeader />

      {/* Filters Section */}
      <div className="bg-white border-b sticky top-[0px] z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          {/* Search Bar */}
          <div className="mb-4">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search courses by name or category..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
              />
            </div>
          </div>

          {/* Category Filter Pills */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
            <FiFilter className="text-gray-500 flex-shrink-0" />
            <button
              onClick={() => fetchCoursesByCategory('all')}
              className={`px-4 py-2 rounded-full font-medium whitespace-nowrap transition-all ${
                selectedCategory === 'all'
                  ? 'bg-primary-500 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All Courses
            </button>
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => fetchCoursesByCategory(category.id)}
                className={`px-4 py-2 rounded-full font-medium whitespace-nowrap transition-all ${
                  selectedCategory === category.id
                    ? 'bg-primary-500 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category.type}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Courses Grid */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Results Count */}
        {!loading && (
          <div className="mb-6">
            <p className="text-gray-600">
              Found <span className="font-semibold text-gray-800">{filteredCourses.length}</span> courses
            </p>
          </div>
        )}

        {loading ? (
          <div className="text-center py-20">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary-500 mx-auto"></div>
            <p className="text-gray-600 mt-4 font-medium">Loading courses...</p>
          </div>
        ) : filteredCourses.length === 0 ? (
          <div className="card text-center py-16">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiBook className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">No Courses Found</h3>
            <p className="text-gray-600 mb-6">Try a different category or search term</p>
            <button
              onClick={() => {
                setSearchQuery('');
                fetchCoursesByCategory('all');
              }}
              className="btn-primary inline-block"
            >
              Clear Filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCourses.map((course) => {
              const finalPrice = (course.price - (course.price * course.discount / 100)).toFixed(2);
              const isPurchased = course.purchased > 0;

              return (
                <div key={course.id} className="card hover:shadow-xl transition-all duration-300 group">
                  {/* Course Thumbnail */}
                  <Link to={`/course-details/${course.id}`} className="block">
                    <div className="relative mb-4 rounded-lg overflow-hidden">
                    <img
                      src={`${API_BASE_URL}/${course.course_thumbnail}`}
                      alt={course.title}
                      className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%236366f1" width="400" height="300"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="white" font-size="24"%3ECourse%3C/text%3E%3C/svg%3E';
                      }}
                    />
                    
                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                    {/* Discount Badge */}
                    {course.discount > 0 && (
                      <div className="absolute top-3 left-3 bg-gradient-to-r from-orange-500 to-red-500 text-white px-3 py-1.5 rounded-full text-sm font-bold shadow-lg animate-pulse">
                        {course.discount}% OFF
                      </div>
                    )}

                    {/* Purchase Status Badge */}
                    {isPurchased && (
                      <div className="absolute top-3 right-3 bg-green-500 text-white px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 shadow-lg">
                        <FiCheck className="w-4 h-4" />
                        OWNED
                      </div>
                    )}
                    </div>
                  </Link>

                  {/* Course Info */}
                  <div>
                    <Link to={`/course-details/${course.id}`}>
                      <h3 className="text-lg font-bold text-gray-800 mb-2 group-hover:text-primary-500 transition-colors line-clamp-2 min-h-[56px]">
                        {course.title}
                      </h3>
                    </Link>
                    
                    {course.description && (
                      <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                        {course.description.replace(/(<([^>]+)>)/gi, '')}
                      </p>
                    )}

                    {/* Meta Info */}
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs bg-blue-50 text-blue-700 px-3 py-1.5 rounded-full font-semibold flex items-center gap-1">
                        <FiTag className="w-3 h-3" />
                        {course.tradeName || 'General'}
                      </span>
                      {course.validity && (
                        <span className="text-xs text-gray-500 flex items-center gap-1">
                          <FiClock className="w-3 h-3" />
                          {course.validity} days
                        </span>
                      )}
                    </div>

                    {/* Price Section */}
                    <div className="flex items-center gap-2 mb-4 bg-gradient-to-r from-primary-50 to-purple-50 -mx-4 px-4 py-3 rounded-lg">
                      <div className="flex items-baseline gap-2">
                        <span className="text-2xl font-bold text-primary-600">
                          ₹{finalPrice}
                        </span>
                        {course.discount > 0 && (
                          <>
                            <span className="text-sm text-gray-500 line-through">
                              ₹{course.price.toFixed(2)}
                            </span>
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded font-semibold">
                              Save ₹{(course.price - finalPrice).toFixed(2)}
                            </span>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Action Button */}
                    {isPurchased ? (
                      <Link
                        to={`/course/${course.id}`}
                        state={{ course }}
                        className="btn-primary w-full flex items-center justify-center bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700"
                      >
                        <FiBook className="mr-2" />
                        View Course
                      </Link>
                    ) : (
                      <Link
                        to={`/course-details/${course.id}`}
                        className="btn-primary w-full flex items-center justify-center bg-gradient-to-r from-primary-500 to-purple-600 hover:from-primary-600 hover:to-purple-700 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all"
                      >
                        <FiShoppingCart className="mr-2" />
                        Buy Now - ₹{finalPrice}
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default AllCourses;

